import { useMemo, useState } from "react";
import { useAtom, useSetAtom } from "jotai";
import { Device } from "common/device";
import { getCountryCallingCode, getCountries, type Country } from "react-phone-number-input";
import { recoverDataAtom, pushRecoverStepAtom, RecoverData } from "store/recoverStore";
import { IdentityNameTelecomPhoneForm, IdentityNameTelecomPhoneResult } from "components/identity/IdentityNameTelecomPhoneForm";
import { IdentityGlobalPhoneForm, IdentityGlobalPhoneResult } from "components/identity/IdentityGlobalPhoneForm";

type SetData = (updater: (prev: RecoverData) => RecoverData) => void;

/**
 * Step2: 본인인증 정보 입력
 *   - KR  → 이름 + 통신사 + 전화번호 (다날)
 *   - 해외 → 국가코드 + 전화번호 (SMS OTP)
 *
 * KR/해외에 따라 별도 inner 컴포넌트로 분기 (hook 일관성 유지).
 */
export const RecoverStep2 = () => {
    const pushStep = useSetAtom(pushRecoverStepAtom);
    const [data, setData] = useAtom(recoverDataAtom);
    const isKorea = useMemo(() => Device.countryCd === "KR", []);

    if (isKorea) {
        return <KoreaForm data={data} setData={setData} pushStep={pushStep} />;
    }
    return <GlobalForm data={data} setData={setData} pushStep={pushStep} />;
};

interface InnerProps {
    data: RecoverData;
    setData: SetData;
    pushStep: (step: number) => void;
}

const KoreaForm = ({ data, setData, pushStep }: InnerProps) => {
    const handleNext = (result: IdentityNameTelecomPhoneResult) => {
        setData(prev => ({
            ...prev,
            name: result.name,
            telecom: result.telecom,
            ctn: result.phone,
            ctnCountryCd: "+82",
            ctnCountry: "KR",
            nationality: result.nationality,
        }));
        pushStep(3);
    };

    return (
        <IdentityNameTelecomPhoneForm
            initialName={data.name}
            initialTelecom={data.telecom}
            initialPhone={data.ctn || ""}
            initialNationality={data.nationality}
            enableNationality
            titleI18nKey="recover.step1.title"
            infoI18nKey="recover.step1.infoKr"
            onNext={handleNext}
        />
    );
};

const GlobalForm = ({ data, setData, pushStep }: InnerProps) => {
    // 진입 시 국가 1회만 derive.
    //   1순위: 이전에 picker 로 고른 ISO2(ctnCountry).
    //   2순위: ctnCountryCd → ISO2 역산. calling code 는 국가를 특정하지 못하므로
    //          (+1 = US/CA 등 25개국, +7 = RU/KZ) find() 가 알파벳순 첫 국가를 집는다 —
    //          +1 이면 AG(앤티가 바부다). ctnCountry 가 없는 경우의 최후 수단으로만 둔다.
    //   3순위: 거주 국가 → US.
    const [initialCountry] = useState<Country>(() => {
        if (data.ctnCountry && getCountries().includes(data.ctnCountry as Country)) {
            return data.ctnCountry as Country;
        }
        if (data.ctnCountryCd) {
            const callingCode = data.ctnCountryCd.replace("+", "");
            const matched = getCountries().find(c => getCountryCallingCode(c) === callingCode);
            if (matched) return matched;
        }
        if (Device.countryCd) {
            const countries = getCountries();
            if (countries.includes(Device.countryCd as Country)) {
                return Device.countryCd as Country;
            }
        }
        return "US" as Country;
    });

    const handleNext = (result: IdentityGlobalPhoneResult) => {
        setData(prev => ({
            ...prev,
            ctn: result.phone,
            ctnCountryCd: result.ctnCountryCd,
            // picker 선택을 ISO2 로 보존 — Step3 갔다 돌아왔을 때 역산 없이 복원한다.
            ctnCountry: result.country,
        }));
        pushStep(3);
    };

    return (
        <IdentityGlobalPhoneForm
            initialCountry={initialCountry}
            initialPhone={data.ctn || ""}
            titleI18nKey="recover.step1.title"
            infoI18nKey="recover.step1.infoGlobal"
            onNext={handleNext}
        />
    );
};
